import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../../context/LanguageContext";
import { useAlert } from "../../../Hooks/useAlert";
import { customerAPI } from "../../../services/api";
import settingsLanguage from "../../../language/settingsLanguage";
import Alert from "../../common/Alert";
import ConfirmDialog from "../../common/ConfirmDialog";
import BackButton from "./BackButton";

function CustomerListPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const text = settingsLanguage[language];
  const { showAlert, showConfirm, alertConfig, confirmConfig } = useAlert();
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const response = await customerAPI.getCustomers();
      if (response.success) {
        setCustomers(response.data);
      }
    } catch (error) {
      console.error("Error loading customers:", error);
    }
  };

  const filteredCustomers = useMemo(
    () =>
      customers.filter(
        (c) =>
          c.customerName.toLowerCase().includes(search.toLowerCase()) ||
          (c.whatsAppNo && c.whatsAppNo.includes(search))
      ),
    [search, customers]
  );

  const handleEditCustomer = (customer) => {
    navigate("/settings/customer/add", { state: { editCustomer: customer, openSection: "customer" } });
  };

  const handleDeleteCustomer = async (customer) => {
    const confirmed = await showConfirm({
      type: "error",
      title: "Delete Customer",
      message: `Are you sure you want to delete ${customer.customerName}? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    if (!confirmed) return;

    try {
      setLoading(true);
      const response = await customerAPI.deleteCustomer(customer.customerId);
      if (response.success) {
        await showAlert({
          type: "success",
          title: "Success",
          message: "Customer deleted successfully!",
        });
        await loadCustomers();
      } else {
        await showAlert({
          type: "error",
          title: "Error",
          message: response.message || "Failed to delete customer",
        });
      }
    } catch (error) {
      console.error("Error deleting customer:", error);
      await showAlert({
        type: "error",
        title: "Error",
        message: "Error deleting customer.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-page">
      <header className="settings-page-header">
        <BackButton to="/settings" section="customer" />
        <h1 className="settings-page-title">{text.customerListPageTitle}</h1>
      </header>

      <main className="settings-page-content">
        <div className="settings-search-bar">
          <i className="fas fa-search" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={text.searchNamePhonePlaceholder}
            className="settings-search-input"
          />
        </div>

        <div className="settings-table-card">
          {filteredCustomers.length > 0 ? (
            <div className="settings-table-wrapper">
              <table className="settings-table">
                <thead>
                  <tr>
                    <th className="settings-table-th settings-table-th-center">{text.actions}</th>
                    <th className="settings-table-th">{text.nameColumn}</th>
                    <th className="settings-table-th">{text.whatsappColumn}</th>
                    <th className="settings-table-th">{text.milkType}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((customer, index) => (
                    <tr key={customer.customerId} className={`settings-table-tr ${index % 2 === 1 ? "settings-table-tr-zebra" : ""}`}>
                      <td className="settings-table-td settings-table-td-center">
                        <div className="settings-table-actions">
                          <button
                            type="button"
                            onClick={() => handleEditCustomer(customer)}
                            className="settings-action-btn settings-action-btn-edit"
                            title={text.edit}
                          >
                            <i className="fas fa-pen" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteCustomer(customer)}
                            disabled={loading}
                            className="settings-action-btn settings-action-btn-delete"
                            title={text.delete}
                          >
                            <i className="fas fa-trash-alt" />
                          </button>
                        </div>
                      </td>
                      <td className="settings-table-td settings-table-name">{customer.customerName}</td>
                      <td className="settings-table-td">{customer.whatsAppNo || "N/A"}</td>
                      <td className="settings-table-td">
                        <span
                          className={`milk-badge ${
                            customer.milkType === 0 ? "milk-badge-cow" : "milk-badge-buffalo"
                          }`}
                        >
                          {customer.milkType === 0 ? text.milkTypeCow : text.milkTypeBuffalo}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="settings-empty-state">
              <i className="fas fa-users" />
              <p>{search ? text.noCustomersMatchSearch : text.noCustomersYet}</p>
            </div>
          )}
        </div>

        {filteredCustomers.length > 0 && (
          <p className="settings-count-text">
            {text.showingCustomers.replace("{count}", filteredCustomers.length).replace("{total}", customers.length)}
          </p>
        )}
      </main>

      {alertConfig && <Alert {...alertConfig} />}
      {confirmConfig && <ConfirmDialog {...confirmConfig} />}
    </div>
  );
}

export default CustomerListPage;
